import { clientInfoFields } from "./clientInfo";
import { findService } from "./discoveryClient";

export async function errorMessage(response: Response, fallback: string): Promise<string> {
  const body: { errors?: { error_message?: string }[] } | null = await response.json().catch(() => null);
  return body?.errors?.[0]?.error_message ?? fallback;
}

/**
 * `POST /auth/login` on this instance's own `nodeowner-auth-api-local`
 * (found fresh via discovery-api each call, not cached -- its address can
 * change over a long session, same reasoning as kwms-web's own
 * discoveryClient usage). `credentials: "include"` -- the response sets
 * the HttpOnly `nodeowner_session` cookie every other authApiLocalClient.ts
 * call authenticates with; without this the browser wouldn't store it.
 * `false` means "no access here" (not provisioned as an auth.authuser) --
 * an expected, unremarkable outcome, not every globally-authenticated
 * person runs this owner's cabinet, same as kwms-web's own globalLogin.
 */
export async function globalLogin(accessToken: string): Promise<boolean> {
  const auth = await findService("auth");
  const response = await fetch(`${auth.base_url}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    credentials: "include",
    body: JSON.stringify({ client_request_uid: crypto.randomUUID(), ...clientInfoFields() }),
  });
  return response.ok;
}

export interface SessionSummary {
  session_uid: string;
  created_at: string;
  expires_at: string;
  client_platform: string | null;
  client_version: string | null;
  client_device_id: string | null;
}

/**
 * `GET /sessions/mine` -- cookie-authenticated. Used as the "am I actually
 * logged in right now" check on app load: the cookie is the single source
 * of truth (no separate silent-refresh/localStorage token dance like
 * kwms-web's own tokenStore.ts -- not needed yet at this app's current
 * scope, revisit if a 13h-shift-length silent-refresh need ever shows up
 * here too).
 */
export async function fetchMySessions(): Promise<SessionSummary[] | null> {
  const auth = await findService("auth");
  const response = await fetch(`${auth.base_url}/sessions/mine`, { credentials: "include" });
  if (!response.ok) return null;
  const body: { sessions: SessionSummary[] } = await response.json();
  return body.sessions;
}

/**
 * `GET /sessions/current` -- which `auth.authusersession` row this browser's
 * own session cookie belongs to. The cookie is HttpOnly, so this is the only
 * way for the account menu's "Выйти" to learn what to pass to `revokeSession`
 * for itself -- same shape as kwms-web's own authApiLocalClient.
 */
export async function fetchCurrentSessionUid(): Promise<string> {
  const auth = await findService("auth");
  const response = await fetch(`${auth.base_url}/sessions/current`, { credentials: "include" });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Не удалось определить текущую сессию"));
  }
  const body: { session_uid: string } = await response.json();
  return body.session_uid;
}

/** `POST /sessions/{session_uid}/revoke` -- same local-session auth as `fetchMySessions`. */
export async function revokeSession(sessionUid: string): Promise<void> {
  const auth = await findService("auth");
  const response = await fetch(`${auth.base_url}/sessions/${sessionUid}/revoke`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Не удалось завершить сессию"));
  }
}
